import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { createTenantRequest } from '../services/auth.service';

const requestAccessSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().optional(),
  suggestedTenantName: z.string().min(2, 'Tenant name is required'),
  note: z.string().optional(),
});

type RequestAccessFormData = z.infer<typeof requestAccessSchema>;

const RequestAccess: React.FC = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestAccessFormData>({
    resolver: zodResolver(requestAccessSchema),
  });

  const onSubmit = async (data: RequestAccessFormData) => {
    try {
      setIsLoading(true);
      setError('');
      await createTenantRequest(data);
      setSubmitted(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        t('pages.requestAccess.errors.submit', 'Failed to submit request')
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <div className="w-full max-w-md">
          <Card className="border-green-200 dark:border-green-900">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-green-900 dark:text-green-100">
                {t('pages.requestAccess.success.title', 'Request Submitted')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-sm text-muted-foreground">
                {t(
                  'pages.requestAccess.success.description',
                  'Your access request has been submitted. Our team will review it and send you an invitation link via email.'
                )}
              </p>
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-xs text-blue-900 dark:text-blue-100">
                  {t(
                    'pages.requestAccess.success.checkEmail',
                    'Check your email for further instructions. The invitation link will expire in 7 days.'
                  )}
                </p>
              </div>
              <Button asChild className="w-full" variant="outline">
                <Link to="/login">{t('pages.requestAccess.success.backToLogin', 'Back to Login')}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <div className="w-full max-w-md space-y-6">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
              <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <CardTitle>{t('pages.requestAccess.title', 'Request Access')}</CardTitle>
            <CardDescription>
              {t(
                'pages.requestAccess.description',
                'Submit your information and we will review your request'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="error" className="mb-4">
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('pages.requestAccess.form.name', 'Your Name')} *
                </Label>
                <Input
                  id="name"
                  placeholder={t('pages.requestAccess.form.namePlaceholder', 'John Doe')}
                  autoFocus
                  {...register('name')}
                  disabled={isLoading}
                />
                {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  {t('pages.requestAccess.form.email', 'Email Address')} *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('pages.requestAccess.form.emailPlaceholder', 'you@company.com')}
                  {...register('email')}
                  disabled={isLoading}
                />
                {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">
                  {t('pages.requestAccess.form.phone', 'Phone (optional)')}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  {...register('phone')}
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="suggestedTenantName">
                  {t('pages.requestAccess.form.tenantName', 'Company Name')} *
                </Label>
                <Input
                  id="suggestedTenantName"
                  placeholder={t('pages.requestAccess.form.tenantNamePlaceholder', 'Acme Corp')}
                  {...register('suggestedTenantName')}
                  disabled={isLoading}
                />
                {errors.suggestedTenantName && (
                  <p className="text-xs text-red-500">{errors.suggestedTenantName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="note">
                  {t('pages.requestAccess.form.note', 'Add notes (optional)')}
                </Label>
                <Textarea
                  id="note"
                  placeholder={t(
                    'pages.requestAccess.form.notePlaceholder',
                    'Add notes'
                  )}
                  {...register('note')}
                  disabled={isLoading}
                  className="resize-none"
                  rows={3}
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('pages.requestAccess.form.submit', 'Submit Request')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          {t('pages.requestAccess.haveAccount', 'Already have an account?')}{' '}
          <Link
            to="/login"
            className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium hover:underline"
          >
            {t('pages.requestAccess.login', 'Log in')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RequestAccess;
